import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {Box, CircularProgress, Typography} from "@mui/material";
import {Masonry} from "@mui/lab";
import {makeStyles} from "tss-react/mui";
import {PageBase} from "@/app2/shared/layouts/PageBase";
import {PageTitle} from "@/app2/shared/components/pageTitle";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {useSearchNotes} from "@/app2/shared/data";
import type {NotesControllerSearchNotesContentType} from "@/app2/shared/data";
import {NotesFilter} from "./NotesFilter";
import {NoteItem} from "./NoteItem";

const useStyles = makeStyles()((theme) => ({
	pageBase: {
		[theme.breakpoints.up("lg")]:{
			"& [class*='menuIcon']": {
				display: "none"
			}
		}
	},
	container:{
		paddingBottom: "70px",
		maxWidth: "1220px",
		width: "calc(100% - 20px)",
	},
	notesMasonry: {
		margin: 0
	},
	loadingContainer: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		padding: theme.spacing(10, 0)
	},
	emptyState: {
		textAlign: "center",
		padding: theme.spacing(10, 0),
		color: theme.palette.text.secondary
	}
}));

export const MyNotesPage: React.FC = () => {
	const navigate = useNavigate();
	const {classes} = useStyles();
	const {t} = useSSTranslation();
	
	// Search state
	const [searchTerm, setSearchTerm] = useState("");
	const [contentType, setContentType] = useState<NotesControllerSearchNotesContentType | undefined>();

	// Fetch notes with search params
	const {data, isLoading} = useSearchNotes({
		searchTerm: searchTerm || undefined,
		contentType
	});

	const handleGoToModule = (courseSlug?: string, moduleSlug?: string, moduleType?: string, timestamp?: number) => {
		if (courseSlug && moduleSlug && moduleType) {
			navigate(`/course/${courseSlug}/${moduleType}/${moduleSlug}`, {state: {timestamp}});
		}
	};

	return (
		<PageBase className={classes.pageBase} contentWidth="1420px" disableSideBar>
			<PageTitle
				sx={{margin: "0 0 0 20px"}}
				applyNewTitleStyle={true}
				title={t("page_notes_title", "Notes library")}
			/>
			<Box className={classes.container}>
				
				<NotesFilter
					searchTerm={searchTerm}
					contentType={contentType}
					onSearchChange={setSearchTerm}
					onContentTypeChange={(v) => setContentType(v as NotesControllerSearchNotesContentType | undefined)}
				/>

				{isLoading ? (
					<Box className={classes.loadingContainer}>
						<CircularProgress />
					</Box>
				) : data && data.notes.length > 0 ? (
					<Masonry className={classes.notesMasonry} columns={{xs: 1, sm: 2, md:3, lg:4}} spacing={3}>
						{data.notes.map((note) => (
							<NoteItem 
								key={note.id} 
								note={note}
								onGoToModuleAction={handleGoToModule}
							/>
						))}
					</Masonry>
				) : (
					<Box className={classes.emptyState}>
						<Typography variant="h6">
							No notes found
						</Typography>
						<Typography variant="body2">
							{searchTerm || contentType
								? "Try adjusting your filters"
								: "Start taking notes during your lessons"}
						</Typography>
					</Box>
				)}
			</Box>
		</PageBase>
	);
}